import _ from 'lodash';
import { useContext, useEffect, useState } from 'react';
import LegoAdminPageContext from '../../legoAdminPageContext';
import ExplainWithAIButton from './ExplainWithAIButton';
import BadgeId from '../../../components/common/BadgeId';
import { FragmentFactoids } from './FragmentFactoids';
import SpecSourcesList from './SpecSourcesList';
import { IconButton } from '../../../components/common/IconButton';
import AIModelSelector from './AIModelSelector';
import StackedBadgeList from './StackedBadgeList';
import SpecGroupBadge from './SpecGroupBadge';
import { FragmentContent } from './FragmentContent';


export default function FragmentsPreview({ fragmentIds = null, fragments: initialFragments = null, specRegex = null }) {
  const { page } = useContext(LegoAdminPageContext);

  const [fragments, setFragments] = useState(initialFragments);
  const [model, setModel] = useState(null);
  useEffect(() => {
    if (fragmentIds && !initialFragments) {
      fetchFragments();
    }
  }, [fragmentIds]);

  useEffect(() => {
    if (initialFragments !== fragments) {
      setFragments(initialFragments);
    }
  }, [initialFragments]);

  function fetchFragments() {
    page.service('services/cerokm/sourcefragment').find({ query: { _id: { $in: fragmentIds || fragments.map(f => f._id) }, $populate: true } }).then(setFragments);
  }

  function reparse(fragment) {
    page.runAsync(page.service('services/cerokm/ai/process-fragments').create({ fragmentIds: [fragment._id], model }).then((res) => {
      console.log(res);
      if (res.parseStats?.errors?.length > 0) {
        page.showNotification({
          title: `Errors reparsing fragment ${fragment._id}`,
          body: `${res.parseStats.errors.map(e => e.error).join(', ')}`,
          level: 'error'
        });
      }
      fetchFragments();
    }), 'Reprocessing fragment');
  }

  function deleteFactoid(fragment) {
    page.runAsync(page.service('services/cerokm/sourcefactoid').remove({ query: { sourceFragmentId: fragment._id } }).then(() => fetchFragments()), 'Deleting factoid');
  }

  async function updateSpecGroups(fragment) {
    await page.runAsync(page.runServerScript('SaveSpecs', { specsGroupIds: fragment.specsGroupIds.join(',') }), `Updating ${fragment.specsGroupIds.length} spec groups...`);
  }

  async function toggleDisableFragments(fragment, disable) {
    const operation = disable ? { $set: { disabled: true } } : { $unset: { disabled: true } };

    if (disable) {
      let noteText = await page.prompt("Enter a note explaining why you are disabling the fragment");
      if (noteText) {
        operation.$push = {
          notes: {
            content: noteText,
            userId: page.getLoggedUserSignature().id,
            createdAt: new Date()
          }
        }
      }
    }

    await page.runAsync(async () => {
      await page.service('services/cerokm/sourcefragment').update({ query: { _id: fragment._id } }, operation);
      fetchFragments();
    }, `${disable ? 'Disabling' : 'Enabling'} fragment`);

    await updateSpecGroups(fragment);
  }


  return <div>
    <table className="table table-sm">
      <thead>
        <tr>
          <th>Text/Id</th>
          <th>Source</th>
          <th>Specs groups</th>
          <th>Type</th>
          <th colSpan={2}>
            Factoids specs
            <span className='ml-5 d-inline-block' style={{ width: '200px' }}>
              <AIModelSelector value={model} onChange={setModel} />
            </span>
          </th>
        </tr>
      </thead>
      <tbody>
        {_.map(fragments, (f) => {
          const { _id, text, type, sourceIds, disabled, specsGroupIds, factoids } = f;

          const disableFragmentsBtn = !disabled ?
            <IconButton level={'danger'} icon={'not_interested'}
              onClick={() => toggleDisableFragments(f, true)} title='Disable fragment' /> :
            <IconButton level={'success'} icon={'done'}
              onClick={() => toggleDisableFragments(f, false)} title='Enable fragment' />;

          return (
            <tr key={_id} className={disabled ? 'translucent bg-light-danger' : ''}>
              <td>{type}<br /><BadgeId id={_id} /></td>

              <td><SpecSourcesList sourceIds={sourceIds} /></td>

              <td>
                <StackedBadgeList>
                  {specsGroupIds.map(id => <div key={id}>
                    <SpecGroupBadge id={id} />
                  </div>)}
                </StackedBadgeList>
              </td>

              <td style={{ width: '30%', fontSize: '13px' }}>
                <FragmentContent fragment={f} />                
              </td>

              <td>
                <FragmentFactoids fragment={f} specRegex={specRegex} />
              </td>

              <td>
                <div className="d-flex align-items-center flex-column">
                  <IconButton level={'success'} icon={'auto_mode'} onClick={() => reparse(f)} title='Reparse fragment' />
                  <IconButton level={'danger'} icon={'delete'} onClick={() => deleteFactoid(f)} title='Delete factoids' />
                  {disableFragmentsBtn}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
}
